import { Injectable, Output, EventEmitter } from '@angular/core';
import { environment } from "environments/environment";
import { HttpClient } from '@angular/common/http';
import { PaymentRejectedRequest } from '../models/payment-rejected-request.model';
import { OriginEnum } from '@sc/pas-common';
import { Router } from '@angular/router';
import { HomeUrlHelper } from '@sc/pas-common';
import { DatePipe } from '../../../shared/pipes';
import { RejectedPaymentEvent } from '../models/rejected-payment-event.model';
import { paymentRejectType } from '../components/constants/payment-reject-type';
import { RejectedPayment } from '../models/rejected-payment.model';
import { SendMailRejectionRequest } from '../models/send-mail-rejection-request.model';
import { MainSideBarService } from 'app/components/shared/main-side-bar/main-side-bar.service';
import { AnalyticsService } from '@sc/pas-common';
import { saveAs } from 'file-saver'
import { map, tap } from 'rxjs/operators';
import { PrimaryInsuredResponse } from '../models/responses/primary-insured-response.model';
import { RejectShareRequest } from '../models/reject-share-request.model';
import { ShareToInsuredRequest } from '../models/share-to-insured-request.model';
import { ShareToInsuredResponse } from '../models/responses/share-to-insured-response.model';

@Injectable({
    providedIn: 'root'
})

export class RejectionSearchService {
    @Output() isOpen: EventEmitter<RejectedPayment>;
    showAlertMessage: boolean;
    lastSearched: string;
    shareToInsuredResponse: ShareToInsuredResponse[] = [];

    constructor(private httpClient: HttpClient,
        private router: Router,
        private datePipe: DatePipe,
        private mainSideBarService: MainSideBarService,
        private analyticsService: AnalyticsService,
        private urlHelper: HomeUrlHelper) {
        this.isOpen = new EventEmitter();
        this.showAlertMessage = true;
    }

    doSearch(request: PaymentRejectedRequest) {
        return this.httpClient.post<any>(`${environment.server.api.portalpas}/Payments/rejected-list`, request);
    }

    getPrimaryInsured(policyNumber: string) {
        return this.httpClient.get<PrimaryInsuredResponse>(`${environment.server.api.portalpas}/Policy/primary-insured?policyNumber=${policyNumber}`);
    }

    addRejectShare(rejectShareRequest: RejectShareRequest) {
        return this.httpClient.post<any>(`${environment.server.api.portalpas}/Payments/rejected-action`, rejectShareRequest);
    }

    shareToInsured(rejectShareRequest: ShareToInsuredRequest) {
        return this.httpClient.post(`${environment.server.api.portalpas}/Payments/share-to-insured`, rejectShareRequest).pipe(
            tap(
                (response: ShareToInsuredResponse) => {
                    response.couponNumber = rejectShareRequest.couponNumber;
                    this.shareToInsuredResponse.push(response);
                }
            )
        );
    }

    exportExcel(fileStartName: string, request: PaymentRejectedRequest) {
        const filename = `${fileStartName}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
        return this.httpClient.post(`${environment.server.api.portalpas}/Payments/export`, request, { responseType: 'blob' }).pipe(
            map(response => {
                saveAs(response, filename);
            })
        );
    }

    sendMail(request: SendMailRejectionRequest) {
        return this.httpClient.post<any>(`${environment.server.api.portalpas}/Mailing/send-mail-rejection`, request);
    }

    public tryNewVersion() {
        return this.httpClient.post(`${environment.server.api.portalpas}/featuretoggle/rejections`, {}).subscribe(() => {
            const url = 'polizas/rechazos';
            this.mainSideBarService.forceMenuReload(url);
            this.router.navigate([url]);
        });
    }

    public returnToOldVersion(user, reason) {
        const data = {
            message: reason,
            dateTime: new Date().toISOString(),
            userId: user.UserId,
            userName: user.userName
        }

        this.analyticsService.AppInsights.event('volver_rechazos_legado', data);

        return this.httpClient.delete(`${environment.server.api.portalpas}/featuretoggle/rejections`).subscribe(() => {
            const url = 'mi-cuenta/rechazos';
            this.mainSideBarService.forceMenuReload(url);
            this.router.navigate([url]);
        });
    }

    openPolicyDetails(policyNumber: string) {
        if (policyNumber.substring(6, 8) === '06' || (policyNumber.substring(6, 8) === '01')) {
            window.open(`/detalle-poliza/ver/${policyNumber}/${OriginEnum.rejectionList}`, '_blank');
            return;
        } else {
            let url = this.urlHelper.urlByOrigin(`/PortalSCNet/PortalPas/AccesoAPortalPas.aspx?Destino=PolizaIndividual&casa=${policyNumber.split('-')[1]}&ramo=${policyNumber.split('-')[2]}&polizaNro=${policyNumber.split('-')[3]}`, 'LEGACY_BASE');
            this.urlHelper.addTokenUrlItem(url, 'NUEVO_PORTAL_TOKEN').subscribe(
                responseUrl => {
                    window.open(responseUrl, '_blank', 'toolbar=0,location=0,menubar=0');
                }
            );
        }
    }

    getPrimaryRejectedPaymentEvents(listRejectedPaymentEvent: RejectedPaymentEvent[]) {
        let response = ''
        if (listRejectedPaymentEvent.length > 0) {
            listRejectedPaymentEvent.sort((a, b) => b.attempt - a.attempt)
            let rejectedPaymentEvent = listRejectedPaymentEvent[0]
            response = `${paymentRejectType.get(rejectedPaymentEvent.paymentRequestRejectType.description)}: ${this.datePipe.transform(rejectedPaymentEvent.rejectDate)}`;
        }
        return response;
    }

    getPrimaryRejectedPaymentType(listRejectedPaymentEvent: RejectedPaymentEvent[]): string {
        let response = ''
        if (listRejectedPaymentEvent.length > 0) {
            listRejectedPaymentEvent.sort((a, b) => b.attempt - a.attempt)
            const rejectedPaymentEvent = listRejectedPaymentEvent[0]
            response = paymentRejectType.get(rejectedPaymentEvent.paymentRequestRejectType.code);
        }
        return response;
    }

    getSecondaryRejectedPaymentEvents(listRejectedPaymentEvent: RejectedPaymentEvent[]) {
        let response = ''
        if (listRejectedPaymentEvent.length > 1) {
            listRejectedPaymentEvent.sort((a, b) => a.attempt - b.attempt)
            let rejectedPaymentEvent = listRejectedPaymentEvent[0]
            if (rejectedPaymentEvent.paymentRequestRejectType) {
                if (rejectedPaymentEvent.rejectDate) {
                    response = `${paymentRejectType.get(rejectedPaymentEvent.paymentRequestRejectType.description)}: ${this.datePipe.transform(rejectedPaymentEvent.rejectDate)}`;
                } else {
                    response = 'Próximo intento programado';
                }
            }

        }

        return response;
    }

    getData(couponNumber: number): ShareToInsuredResponse {
        return this.shareToInsuredResponse.find(element => element.couponNumber === couponNumber);
    }

}
